import { KycDocumentsTokenValidationResponse } from '@/types/kycDocuments.types';
import { apiService } from './api';

/**
 * KYC Documents Service
 * Handles KYC documents-related API operations including token validation
 */
export class KycDocumentsService {
  /**
   * Validates a KYC documents token
   * Public endpoint - no authentication required
   *
   * @param token - The KYC documents token to validate
   * @returns Promise with token validation response including document groups
   */
  async validateKycDocumentsToken(
    token: string
  ): Promise<KycDocumentsTokenValidationResponse> {
    try {
      const response = await apiService.get(
        `/kyc/documents/validate-token/${token}`
      );
      return response.data as KycDocumentsTokenValidationResponse;
    } catch (error: unknown) {
      // Handle API errors
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as {
          response?: {
            data?: {
              message?: string;
              errors?: { error: string };
            };
            status?: number;
          };
        };

        // Handle invalid token (401)
        if (apiError.response?.status === 401) {
          throw new Error(
            apiError.response?.data?.message || 'Token is invalid or expired'
          );
        }

        // Handle server error (500)
        if (apiError.response?.status === 500) {
          throw new Error(
            apiError.response?.data?.message ||
              'Invalid, expired or malformed token'
          );
        }

        throw new Error(
          apiError.response?.data?.message || 'Document token validation failed'
        );
      }
      throw new Error('Network error during document token validation');
    }
  }
}

// Export singleton instance
export const kycDocumentsService = new KycDocumentsService();
